import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { CarbonChart, CHART_HEIGHT } from '../../components/socialdash'
import { categoryColorMap } from '../../components/social-media/design-tokens'
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { OUTPUT_SHARE_CHART, OUTPUT_TAG_KEYS } from './charts'
import { drawnSlices } from './drawnSlices'

export { drawnSlices }

/** Room the tag selector takes under the pie, so the cell matches its neighbour's height. */
const SELECTOR_HEIGHT = 52

export interface OutputShareCellProps {
  /** Pie-ready table from `useSkyOutputOpinionShare`. `colpc` is a 0–1 fraction. */
  data: PlatinumData | null
  totalEntities: number
  isLoading: boolean
  error: Error | null
  /** The post-tag label key the pie is grouped by. */
  tagKey: string
  onTagKeyChange: (key: string) => void
}

/**
 * OUTPUT OPINION SHARE: the share of this department's posts carrying each
 * value of one post tag, drawn through the same pie path as every other chart.
 *
 * The selector exists because Sky has not said which of the three tags they
 * mean. Once they do, the selector goes and the page passes the one key.
 */
export const OutputShareCell: React.FC<OutputShareCellProps> = ({
  data,
  totalEntities,
  isLoading,
  error,
  tagKey,
  onTagKeyChange,
}) => (
  <Box data-testid="sky-product-output-share" sx={{ display: 'flex', flexDirection: 'column' }}>
    <CarbonChart
      definition={OUTPUT_SHARE_CHART}
      data={data}
      isLoading={isLoading}
      error={error?.message ?? null}
      height={CHART_HEIGHT - SELECTOR_HEIGHT}
      // A tag's values are free-form: the line charts' pastels, never the default palette.
      colorMap={categoryColorMap(drawnSlices(data))}
      useGradients={false}
    />
    <Box
      sx={{
        height: SELECTOR_HEIGHT,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="body2" color="text.secondary" noWrap>
        {data ? `Share of ${totalEntities.toLocaleString()} post${totalEntities !== 1 ? 's' : ''}` : ''}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        color="info"
        value={tagKey}
        onChange={(_, next: string | null) => {
          if (next) onTagKeyChange(next)
        }}
        aria-label="Group posts by"
      >
        {OUTPUT_TAG_KEYS.map((t) => (
          <ToggleButton
            key={t.key}
            value={t.key}
            data-testid={`sky-output-share-tag-${t.key}`}
            sx={{ textTransform: 'none', py: 0.25, px: 1.25 }}
          >
            {t.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  </Box>
)

export default OutputShareCell
